import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import WeeklyAverageYearChart from './WeeklyAverageYearChart';
import {
  PRECIO_OFICIAL, PALETTE, SPANISH_MONTHS_SHORT, SPANISH_MONTHS_LONG,
  fmtCurrency, fmtDollars, fmtPct, titleCase,
  fmtDateLong, cellColor, buildPriceInsight, aggregateDaily,
  formatRelativeTime, freshnessLevel, summarizeFreshness,
} from './chartShared';
import './WeeklyAverageYearChart.css';
import './PriceRangeChart.css';

const PERIODS = [
  { id: '7',   label: 'Esta semana', days: 7   },
  { id: '30',  label: 'Este mes',    days: 30  },
  { id: '90',  label: '3 meses',     days: 90  },
  { id: '365', label: '1 año',       days: 365 },
];

const REFRESH_MS = 5 * 60 * 1000; // poll every 5 min

const ZONE_COLOR = {
  norte: PALETTE.green,    // verde marca
  sur:   PALETTE.blue,     // azul marca
};

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoBefore = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const fetchZoneData = async (zone, start, end, token) => {
  const url = `${process.env.REACT_APP_API_URL}/prices/range/?zone=${zone}&start_date=${start}&end_date=${end}`;
  const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
  return [...res.data].sort((a, b) => new Date(a.date) - new Date(b.date));
};

const PriceRangeChart = () => {
  const { t } = useTranslation();
  const [zone, setZone]         = useState('norte'); // 'norte' | 'sur' | 'both'
  const [periodId, setPeriodId] = useState('30');
  const [dataByZone, setDataByZone] = useState({ norte: [], sur: [] });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [, setNowTick]          = useState(0); // re-render every minute for relative time

  const chartContainerRef = useRef(null);
  const tooltipRef        = useRef(null);

  const period = PERIODS.find(p => p.id === periodId) ?? PERIODS[1];
  const isBoth = zone === 'both';

  // ── Fetch data (with auto-refresh every 5 min) ──────────
  useEffect(() => {
    let cancelled = false;
    const run = async (silent = false) => {
      const token = localStorage.getItem('access_token');
      if (!token) { setError(t('auth_required')); setLoading(false); return; }
      if (!silent) setLoading(true);
      try {
        const start = isoBefore(period.days);
        const end   = isoToday();
        const zonesToFetch = isBoth ? ['norte', 'sur'] : [zone];
        const results = await Promise.all(zonesToFetch.map(z => fetchZoneData(z, start, end, token)));
        if (cancelled) return;
        const next = { norte: [], sur: [] };
        zonesToFetch.forEach((z, i) => { next[z] = results[i]; });
        setDataByZone(next);
        setError(null);
      } catch (err) {
        console.error('Error fetching price range:', err);
        if (!silent) setError(t('range_chart_error'));
      } finally {
        if (!silent && !cancelled) setLoading(false);
      }
    };
    run();
    const interval = setInterval(() => run(true), REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [t, zone, period.days, isBoth]);

  // Tick every 60s so "hace X minutos" stays current without refetch
  useEffect(() => {
    const id = setInterval(() => setNowTick(n => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // ── Derived series per zone ─────────────────────────────
  const seriesNorte = useMemo(() => aggregateDaily(dataByZone.norte), [dataByZone.norte]);
  const seriesSur   = useMemo(() => aggregateDaily(dataByZone.sur),   [dataByZone.sur]);
  const series      = isBoth ? seriesNorte : (zone === 'sur' ? seriesSur : seriesNorte);

  const stats = useMemo(() => {
    if (!series.length) return null;
    const today = series[series.length - 1];
    const prev  = series.length > 1 ? series[series.length - 2] : null;
    const diff  = prev ? today.value - prev.value : 0;
    const diffOficial    = today.value - PRECIO_OFICIAL;
    const diffOficialPct = (diffOficial / PRECIO_OFICIAL) * 100;
    const minPt = series.reduce((a, b) => (b.value < a.value ? b : a));
    const maxPt = series.reduce((a, b) => (b.value > a.value ? b : a));
    return { today, prev, diff, diffOficial, diffOficialPct, minPt, maxPt };
  }, [series]);

  // Dual stats for 'both' mode
  const dualStats = useMemo(() => {
    if (!isBoth) return null;
    const lastN = seriesNorte[seriesNorte.length - 1];
    const lastS = seriesSur[seriesSur.length - 1];
    if (!lastN && !lastS) return null;
    const spread = (lastN && lastS) ? lastN.value - lastS.value : null;
    return { norte: lastN, sur: lastS, spread };
  }, [isBoth, seriesNorte, seriesSur]);

  const insight = useMemo(() => {
    if (isBoth) {
      if (!dualStats || dualStats.spread == null) return null;
      const abs = Math.abs(dualStats.spread);
      if (abs < 0.05) return { icon: '⚖️', text: 'Las dos zonas están casi iguales hoy.' };
      const higher = dualStats.spread > 0 ? 'Norte' : 'Sur';
      return {
        icon: '🌱',
        text: `${higher} está pagando ${fmtDollars(abs)} más que ${dualStats.spread > 0 ? 'Sur' : 'Norte'} hoy.`,
      };
    }
    return buildPriceInsight(series);
  }, [isBoth, dualStats, series]);

  // Latest report timestamp (across all loaded data)
  const freshness = useMemo(() => {
    const all = [...dataByZone.norte, ...dataByZone.sur];
    return summarizeFreshness(all);
  }, [dataByZone]);

  // ── Build weekday-aligned calendar weeks (Mon → Sun) ────
  const calendarWeeks = useMemo(() => {
    const map = new Map(series.map(p => [p.iso, p.value]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(start.getDate() - period.days + 1);

    // Pad to Monday of starting week
    const padStart = new Date(start);
    const padStartDow = (padStart.getDay() + 6) % 7; // Mon=0 ... Sun=6
    padStart.setDate(padStart.getDate() - padStartDow);

    // Pad to Sunday of ending week
    const padEnd = new Date(today);
    const padEndDow = (padEnd.getDay() + 6) % 7;
    padEnd.setDate(padEnd.getDate() + (6 - padEndDow));

    const weeks = [];
    const cursor = new Date(padStart);
    while (cursor <= padEnd) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        const inRange = cursor >= start && cursor <= today;
        const isToday = cursor.getTime() === today.getTime();
        week.push({
          iso,
          date: new Date(cursor),
          value: inRange ? (map.get(iso) ?? null) : undefined,
          inRange,
          isToday,
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [series, period.days]);

  // Month label like "Mayo 2026" or "Abril – Mayo 2026"
  const calendarMonthLabel = useMemo(() => {
    if (!calendarWeeks.length) return '';
    const allDays = calendarWeeks.flat().filter(d => d.inRange);
    if (!allDays.length) return '';
    const first = allDays[0].date;
    const last  = allDays[allDays.length - 1].date;
    const fm = SPANISH_MONTHS_LONG[first.getMonth()];
    const lm = SPANISH_MONTHS_LONG[last.getMonth()];
    const fy = first.getFullYear();
    const ly = last.getFullYear();
    if (fm === lm && fy === ly) return titleCase(`${fm} ${fy}`);
    if (fy === ly) return titleCase(`${fm} – ${lm} ${fy}`);
    return titleCase(`${fm} ${fy} – ${lm} ${ly}`);
  }, [calendarWeeks]);

  // ── Chart lifecycle ─────────────────────────────────────
  useEffect(() => {
    if (!chartContainerRef.current) return undefined;
    const hasN = isBoth ? seriesNorte.length > 0 : false;
    const hasS = isBoth ? seriesSur.length > 0 : false;
    if (!isBoth && !series.length) return undefined;
    if (isBoth && !hasN && !hasS) return undefined;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor:  '#9ba5bc',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        attributionLogo: false,
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { color: '#f0f1f5' },
      },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.18, bottom: 0.15 } },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        tickMarkFormatter: (time) => {
          if (time && typeof time === 'object' && 'year' in time) {
            const m = SPANISH_MONTHS_SHORT[time.month - 1] ?? '';
            return period.days <= 35 ? `${time.day} ${m}` : m;
          }
          return '';
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: '#9ba5bc', width: 1, style: LineStyle.Dotted, labelVisible: false },
        horzLine: { color: '#9ba5bc', width: 1, style: LineStyle.Dotted, labelVisible: false },
      },
      handleScroll: false,
      handleScale:  false,
    });

    const makeAreaSeries = (color, alpha) => chart.addAreaSeries({
      lineColor:    color,
      lineWidth:    3,
      topColor:     `${color}${alpha}`,
      bottomColor:  `${color}00`,
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat:  { type: 'price', precision: 2, minMove: 0.01 },
    });

    // Build the active set of series
    const seriesMap = []; // [{ key, label, color, lwSeries, data }]
    if (isBoth) {
      if (hasN) {
        const s = makeAreaSeries(ZONE_COLOR.norte, '52'); // ~32% alpha
        s.setData(seriesNorte.map(({ time, value }) => ({ time, value })));
        seriesMap.push({ key: 'norte', label: 'Norte', color: ZONE_COLOR.norte, lwSeries: s });
      }
      if (hasS) {
        const s = makeAreaSeries(ZONE_COLOR.sur, '40'); // ~25% alpha (so Norte stays prominent)
        s.setData(seriesSur.map(({ time, value }) => ({ time, value })));
        seriesMap.push({ key: 'sur', label: 'Sur', color: ZONE_COLOR.sur, lwSeries: s });
      }
    } else {
      const color = ZONE_COLOR[zone] || ZONE_COLOR.norte;
      const s = makeAreaSeries(color, '52');
      s.setData(series.map(({ time, value }) => ({ time, value })));
      seriesMap.push({ key: zone, label: zone === 'sur' ? 'Sur' : 'Norte', color, lwSeries: s });
    }

    // Reference line: precio oficial (attached to first series)
    if (seriesMap[0]) {
      seriesMap[0].lwSeries.createPriceLine({
        price: PRECIO_OFICIAL,
        color: '#9ba5bc',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'Oficial',
      });
    }

    // Min / Max markers — only for single zone
    if (!isBoth && stats && seriesMap[0]) {
      seriesMap[0].lwSeries.setMarkers([
        { time: stats.maxPt.time, position: 'aboveBar', color: '#17c671', shape: 'arrowDown', text: `Máx ${fmtCurrency(stats.maxPt.value)}` },
        { time: stats.minPt.time, position: 'belowBar', color: '#f53d6b', shape: 'arrowUp',   text: `Mín ${fmtCurrency(stats.minPt.value)}` },
      ]);
    }

    chart.timeScale().fitContent();

    // Custom tooltip following crosshair
    chart.subscribeCrosshairMove(param => {
      const tip = tooltipRef.current;
      const container = chartContainerRef.current;
      if (!tip || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0 || param.point.x > w || param.point.y > h) {
        tip.style.display = 'none';
        return;
      }
      // Pick a date (any series will share same time scale)
      const probeData = seriesMap.map(s => param.seriesData.get(s.lwSeries)).find(Boolean);
      if (!probeData) { tip.style.display = 'none'; return; }
      const tm = probeData.time;
      const date = (tm && typeof tm === 'object' && 'year' in tm)
        ? new Date(tm.year, tm.month - 1, tm.day)
        : new Date(tm * 1000);
      if (Number.isNaN(date.getTime())) { tip.style.display = 'none'; return; }
      const dayLabel = fmtDateLong(date);

      let body = '';
      if (isBoth) {
        const rows = seriesMap.map(s => {
          const d = param.seriesData.get(s.lwSeries);
          if (!d) return null;
          const diff = d.value - PRECIO_OFICIAL;
          const diffColor = diff >= 0 ? '#17c671' : '#f53d6b';
          return `<div class="prc-tip__row">
            <span class="prc-tip__zone-dot" style="background:${s.color}"></span>
            <span class="prc-tip__zone">${s.label}</span>
            <span class="prc-tip__zone-val">${fmtCurrency(d.value)}</span>
            <span class="prc-tip__zone-diff" style="color:${diffColor}">${diff >= 0 ? '+' : ''}${fmtDollars(diff)}</span>
          </div>`;
        }).filter(Boolean).join('');
        body = rows;
      } else {
        const d = param.seriesData.get(seriesMap[0].lwSeries);
        if (!d) { tip.style.display = 'none'; return; }
        const diff = d.value - PRECIO_OFICIAL;
        const diffLabel = diff >= 0
          ? `<span style="color:#17c671">${fmtDollars(diff)} sobre el oficial</span>`
          : `<span style="color:#f53d6b">${fmtDollars(diff)} bajo el oficial</span>`;
        body = `
          <div class="prc-tip__price">${fmtCurrency(d.value)}</div>
          <div class="prc-tip__diff">${diffLabel}</div>
        `;
      }
      tip.innerHTML = `<div class="prc-tip__date">${dayLabel}</div>${body}`;
      tip.style.display = 'block';
      const tipW = tip.clientWidth;
      const tipH = tip.clientHeight;
      let x = param.point.x + 16;
      let y = param.point.y - tipH - 16;
      if (x + tipW > w) x = param.point.x - tipW - 16;
      if (y < 0) y = param.point.y + 16;
      tip.style.left = `${x}px`;
      tip.style.top  = `${y}px`;
    });

    const handleResize = () => {
      if (!chartContainerRef.current) return;
      chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      chart.timeScale().fitContent();
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [series, stats, isBoth, seriesNorte, seriesSur, zone, period.days]);

  // ── Annual heatmap (GitHub-style) for periods > 35 days ──
  const annualGrid = useMemo(() => {
    if (period.days <= 35) return null;
    const valueMap = new Map(series.map(p => [p.iso, p.value]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(start.getDate() - period.days + 1);

    // Pad to Monday of starting week
    const padStart = new Date(start);
    padStart.setDate(padStart.getDate() - ((padStart.getDay() + 6) % 7));
    // Pad to Sunday of ending week
    const padEnd = new Date(today);
    padEnd.setDate(padEnd.getDate() + (6 - ((padEnd.getDay() + 6) % 7)));

    const weeks = [];
    let lastMonth = null;
    const monthLabels = [];
    let weekIndex = 0;
    const cursor = new Date(padStart);
    while (cursor <= padEnd) {
      const week = [];
      let weekFirstInRange = null;
      for (let i = 0; i < 7; i++) {
        const iso = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        const inRange = cursor >= start && cursor <= today;
        const isToday = cursor.getTime() === today.getTime();
        week.push({
          iso,
          date: new Date(cursor),
          value: inRange ? (valueMap.get(iso) ?? null) : undefined,
          inRange,
          isToday,
        });
        if (inRange && !weekFirstInRange) weekFirstInRange = new Date(cursor);
        cursor.setDate(cursor.getDate() + 1);
      }
      // Detect month change at the start of this week
      if (weekFirstInRange) {
        const m = weekFirstInRange.getMonth();
        if (m !== lastMonth) {
          monthLabels.push({ weekIndex, label: SPANISH_MONTHS_SHORT[m] });
          lastMonth = m;
        }
      }
      weeks.push(week);
      weekIndex++;
    }
    return { weeks, monthLabels };
  }, [series, period.days]);

  if (loading) return <div className="loading">{t('range_chart_loading')}</div>;
  if (error)   return <div className="error">{error}</div>;

  const hasData = isBoth ? (seriesNorte.length > 0 || seriesSur.length > 0) : series.length > 0;
  const freshLevel = freshnessLevel(freshness.latest);

  return (
    <div className="price-range-container">
      {/* Zone selector */}
      <div className="zone-selector-row">
        <h2>{t('range_chart_title')}</h2>
        <div className="zone-tabs">
          <button className={`zone-tab ${zone === 'norte' ? 'zone-tab--active' : ''}`} onClick={() => setZone('norte')}>{t('zona_norte')}</button>
          <button className={`zone-tab ${zone === 'sur'   ? 'zone-tab--active' : ''}`} onClick={() => setZone('sur')}>{t('zona_sur')}</button>
          <button className={`zone-tab ${isBoth ? 'zone-tab--active' : ''}`} onClick={() => setZone('both')}>Ambas</button>
        </div>
      </div>

      <div className="prc-card">
        {/* Freshness bar */}
        {freshness.latest && (
          <div className="prc-freshness">
            <span className={`prc-freshness__dot prc-freshness__dot--${freshLevel}`} />
            <span>
              <span className="prc-freshness__strong">Última actualización:</span> {formatRelativeTime(freshness.latest)}
              {freshness.todayCount > 0 && (
                <> · {freshness.todayCount} {freshness.todayCount === 1 ? 'reporte' : 'reportes'} hoy</>
              )}
            </span>
          </div>
        )}

        {/* Hero — single zone */}
        {!isBoth && hasData && stats && (
          <div className="prc-hero">
            <div className="prc-hero__main">
              <div className="prc-hero__value">{fmtCurrency(stats.today.value)}</div>
              <div className="prc-hero__caption">la caja hoy</div>
            </div>
            <div className="prc-hero__deltas">
              {stats.prev && Math.abs(stats.diff) >= 0.005 && (
                <span className={`prc-delta ${stats.diff >= 0 ? 'prc-delta--up' : 'prc-delta--down'}`}>
                  {stats.diff >= 0 ? '▲' : '▼'} {fmtDollars(stats.diff)} {stats.diff >= 0 ? 'más' : 'menos'} que el día anterior
                </span>
              )}
              <span className={`prc-delta ${stats.diffOficial >= 0 ? 'prc-delta--up' : 'prc-delta--down'}`}>
                {stats.diffOficial >= 0
                  ? `${fmtPct(stats.diffOficialPct)} sobre el precio oficial 🌱`
                  : `${fmtPct(stats.diffOficialPct)} bajo el precio oficial ⚠️`}
              </span>
            </div>
          </div>
        )}

        {/* Hero — dual zone */}
        {isBoth && dualStats && (
          <>
            <div className="prc-hero--dual">
              <div className="prc-hero__zone prc-hero__zone--norte">
                <div className="prc-hero__zone-label">Zona Norte</div>
                <div className="prc-hero__zone-value">
                  {dualStats.norte ? fmtCurrency(dualStats.norte.value) : '—'}
                </div>
              </div>
              <div className="prc-hero__zone prc-hero__zone--sur">
                <div className="prc-hero__zone-label">Zona Sur</div>
                <div className="prc-hero__zone-value">
                  {dualStats.sur ? fmtCurrency(dualStats.sur.value) : '—'}
                </div>
              </div>
            </div>
            {dualStats.spread != null && (
              <div className="prc-hero__spread">
                {Math.abs(dualStats.spread) < 0.05
                  ? 'Spread casi cero — las dos zonas pagan igual hoy'
                  : <>Spread de <strong>{fmtDollars(dualStats.spread)}</strong> a favor del <strong>{dualStats.spread > 0 ? 'Norte' : 'Sur'}</strong></>
                }
              </div>
            )}
          </>
        )}

        {/* Period chips */}
        <div className="prc-chips">
          {PERIODS.map(p => (
            <button
              key={p.id}
              className={`prc-chip ${periodId === p.id ? 'prc-chip--active' : ''}`}
              onClick={() => setPeriodId(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="prc-chart-wrap">
          <div className="prc-chart" ref={chartContainerRef} />
          <div className="prc-tip" ref={tooltipRef} />
          {!hasData && <div className="prc-empty">Aún no hay precios para este período.</div>}
        </div>

        {/* Legend (only when comparing both zones) */}
        {isBoth && hasData && (
          <div className="prc-chart-legend">
            <span className="prc-chart-legend__item">
              <span className="prc-chart-legend__swatch" style={{ background: ZONE_COLOR.norte }} />
              Zona Norte
            </span>
            <span className="prc-chart-legend__item">
              <span className="prc-chart-legend__swatch" style={{ background: ZONE_COLOR.sur }} />
              Zona Sur
            </span>
          </div>
        )}

        {/* Auto-insight */}
        {insight && (
          <div className="prc-insight">
            <span className="prc-insight__icon">{insight.icon}</span>
            <span className="prc-insight__text">{insight.text}</span>
          </div>
        )}
      </div>

      {/* Calendar heatmap (single zone, ≤35 days) */}
      {hasData && !isBoth && period.days <= 35 && (
        <div className="prc-card">
          <div className="prc-cal__header">
            <div>
              <h3>Calendario de precios</h3>
              <div className="prc-cal__sub">{calendarMonthLabel}</div>
            </div>
            <div className="prc-cal__legend">
              <span className="prc-cal__lg"><i style={{ background: '#ef4d6c' }} />Bajo</span>
              <span className="prc-cal__lg"><i style={{ background: '#fbcf6e' }} />Cerca</span>
              <span className="prc-cal__lg"><i style={{ background: '#17c671' }} />Sobre oficial</span>
              <span className="prc-cal__lg"><i style={{ background: '#eef0f5' }} />Sin reporte</span>
            </div>
          </div>

          <div className="prc-cal__weekdays">
            <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
          </div>
          <div className="prc-cal__weeks">
            {calendarWeeks.map((week, wi) => (
              <div key={wi} className="prc-cal__week">
                {week.map(d => (
                  <div
                    key={d.iso}
                    className={`prc-cal__cell ${!d.inRange ? 'prc-cal__cell--out' : ''} ${d.isToday ? 'prc-cal__cell--today' : ''}`}
                    style={d.inRange ? { background: cellColor(d.value) } : undefined}
                    title={
                      d.inRange
                        ? (d.value != null
                            ? `${fmtDateLong(d.date)} · ${fmtCurrency(d.value)}`
                            : `${fmtDateLong(d.date)} · sin reporte`)
                        : undefined
                    }
                  >
                    {d.inRange && (
                      <>
                        <span className="prc-cal__day">{d.date.getDate()}</span>
                        {d.value != null && (
                          <span className="prc-cal__price">{fmtCurrency(d.value)}</span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Annual heatmap (single zone, > 35 days) */}
      {hasData && !isBoth && annualGrid && (
        <div className="prc-card">
          <div className="prc-cal__header">
            <div>
              <h3>Mapa de calor — {period.days} días</h3>
              <div className="prc-cal__sub">Cada cuadrito es un día. Color según precio vs oficial.</div>
            </div>
            <div className="prc-cal__legend">
              <span className="prc-cal__lg"><i style={{ background: '#ef4d6c' }} />Bajo</span>
              <span className="prc-cal__lg"><i style={{ background: '#fbcf6e' }} />Cerca</span>
              <span className="prc-cal__lg"><i style={{ background: '#17c671' }} />Sobre oficial</span>
              <span className="prc-cal__lg"><i style={{ background: '#eef0f5' }} />Sin reporte</span>
            </div>
          </div>

          <div className="prc-yhm">
            <div className="prc-yhm__inner">
              {/* Month labels row */}
              <div className="prc-yhm__months">
                {annualGrid.monthLabels.map((m, i) => (
                  <span
                    key={`${m.weekIndex}-${i}`}
                    className="prc-yhm__month-label"
                    style={{ left: `calc(${m.weekIndex} * (12px + 3px))` }}
                  >
                    {m.label}
                  </span>
                ))}
              </div>
              {/* Weekday rows */}
              <div className="prc-yhm__rows">
                <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
              </div>
              {/* Heatmap grid */}
              <div className="prc-yhm__grid" style={{ gridColumn: 2 }}>
                {annualGrid.weeks.map((week, wi) => (
                  <div key={wi} className="prc-yhm__col">
                    {week.map(d => (
                      <div
                        key={d.iso}
                        className={`prc-yhm__cell ${!d.inRange ? 'prc-yhm__cell--out' : ''} ${d.isToday ? 'prc-yhm__cell--today' : ''}`}
                        style={d.inRange ? { background: cellColor(d.value) } : undefined}
                        title={
                          d.inRange
                            ? (d.value != null
                                ? `${fmtDateLong(d.date)} · ${fmtCurrency(d.value)}`
                                : `${fmtDateLong(d.date)} · sin reporte`)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly average across the year */}
      <WeeklyAverageYearChart />
    </div>
  );
};

export default PriceRangeChart;
